import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Search, ClipboardList, Loader2, ExternalLink, Star } from 'lucide-react';
import { stagingLeadsAPI, dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * "Leads" tab — a standalone staging list of candidate companies (usually
 * competitors of a pre-sales lead). Add them one at a time, via AI web search,
 * or by pasting a list, then import the good ones into Pre-Sales (dedupe skips
 * anything already in the pipeline). Rendered as tables to match Pre-Sales.
 */

const INPUT_CLS = 'w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 mb-1';
const TH_CLS = 'px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase';
const TD_CLS = 'px-4 py-2 text-sm';

const EMPTY_FORM = {
  company_name: '', company_url: '', linkedin_url: '',
  core_business: '', location: '', related_to: '', notes: '', suggested: false
};

const statusPill = (status) => {
  switch (status) {
    case 'imported':  return 'bg-green-100 text-green-700';
    case 'duplicate': return 'bg-yellow-100 text-yellow-700';
    case 'dismissed': return 'bg-gray-100 text-gray-500';
    default:          return 'bg-[#e1f0da] text-[#587f2b]';
  }
};
const statusLabel = (status) => (status === 'pending' ? 'New' : status.charAt(0).toUpperCase() + status.slice(1));

export default function StagingLeads() {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bdas, setBdas] = useState([]);
  const [actionLoading, setActionLoading] = useState({});

  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await stagingLeadsAPI.list();
      if (r?.success) setRows(r.data || []);
      else setError(r?.error || 'Failed to load');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    dashboardAPI.getBDEUsers?.().then(r => setBdas(r?.data || [])).catch(() => {});
    refresh();
  }, [refresh]);

  const myBde = useMemo(() => {
    if (!user?.name || !bdas.length) return null;
    const t = user.name.toLowerCase().trim();
    return bdas.find(b => (b.name || '').toLowerCase().trim() === t)
        || bdas.find(b => (b.name || '').toLowerCase().includes(t))
        || bdas.find(b => t.includes((b.name || '').toLowerCase()))
        || null;
  }, [user, bdas]);

  const provenance = useCallback((extra = {}) => ({
    added_by_id: myBde?.id ?? null,
    added_by_name: user?.name || null,
    added_by_role: isAdmin ? 'admin' : 'bde',
    ...extra
  }), [myBde, user, isAdmin]);

  const addManual = async (e) => {
    e?.preventDefault?.();
    if (!form.company_name.trim()) return;
    setSaving(true);
    try {
      const r = await stagingLeadsAPI.add(provenance({ ...form, source: 'manual', suggested: isAdmin ? form.suggested : false }));
      if (!r?.success) throw new Error(r?.error || 'Add failed');
      setRows(prev => [r.data, ...prev]);
      setForm(EMPTY_FORM);
      setShowManual(false);
    } catch (err) { alert(`Add failed: ${err.response?.data?.error || err.message}`); }
    finally { setSaving(false); }
  };

  const addFromFinder = async (cand, { suggested = false, relatedTo = '' } = {}) => {
    const r = await stagingLeadsAPI.add(provenance({
      company_name: cand.company_name,
      company_url: cand.company_url,
      linkedin_url: cand.linkedin_url,
      core_business: cand.core_business,
      location: cand.location,
      related_to: relatedTo,
      notes: cand.why || '',
      source: 'gemini',
      suggested: isAdmin ? suggested : false
    }));
    if (!r?.success) throw new Error(r?.error || 'Add failed');
    setRows(prev => [r.data, ...prev]);
    return r.data;
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove "${row.company_name}" from the Leads list? This does not touch Pre-Sales.`)) return;
    setActionLoading(s => ({ ...s, [row.id]: true }));
    try {
      await stagingLeadsAPI.remove(row.id);
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (e) { alert(`Remove failed: ${e.response?.data?.error || e.message}`); }
    finally { setActionLoading(s => ({ ...s, [row.id]: false })); }
  };

  const importRow = async (row, bdeId = null) => {
    const assignToBdeId = bdeId ?? myBde?.id ?? null;
    setActionLoading(s => ({ ...s, [row.id]: true }));
    try {
      const r = await stagingLeadsAPI.import(row.id, { assignToBdeId });
      if (!r?.success) throw new Error(r?.error || 'Import failed');
      if (r.duplicate) {
        setRows(prev => prev.map(x => x.id === row.id ? { ...x, status: 'duplicate' } : x));
        alert(`Not imported. "${row.company_name}" is already in ${r.existingIn?.replace('_', ' ')}${r.existingName ? ` (as "${r.existingName}")` : ''}.`);
      } else {
        setRows(prev => prev.map(x => x.id === row.id ? { ...x, status: 'imported' } : x));
      }
    } catch (e) { alert(`Import failed: ${e.response?.data?.error || e.message}`); }
    finally { setActionLoading(s => ({ ...s, [row.id]: false })); }
  };

  const pendingCount = rows.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-4">
      {/* Intro + manual add */}
      <div className="bg-white rounded shadow border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Leads</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Companies to look into — usually competitors of a lead. Import the good ones into Pre-Sales; duplicates are skipped.
            </p>
          </div>
          <button onClick={() => setShowManual(v => !v)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm">
            <Plus size={16} /> Add one
          </button>
        </div>

        {showManual && (
          <form onSubmit={addManual} className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLS}>Company name *</label>
              <input className={INPUT_CLS} value={form.company_name} required
                onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="e.g. EDF Renewables" />
            </div>
            <div>
              <label className={LABEL_CLS}>Competitor of</label>
              <input className={INPUT_CLS} value={form.related_to}
                onChange={e => setForm(f => ({ ...f, related_to: e.target.value }))} placeholder="e.g. H2air" />
            </div>
            <div>
              <label className={LABEL_CLS}>Core business</label>
              <input className={INPUT_CLS} value={form.core_business}
                onChange={e => setForm(f => ({ ...f, core_business: e.target.value }))} placeholder="Solar IPP, BESS operator" />
            </div>
            <div>
              <label className={LABEL_CLS}>Company URL</label>
              <input className={INPUT_CLS} value={form.company_url}
                onChange={e => setForm(f => ({ ...f, company_url: e.target.value }))} placeholder="https://" />
            </div>
            <div>
              <label className={LABEL_CLS}>LinkedIn URL</label>
              <input className={INPUT_CLS} value={form.linkedin_url}
                onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/company/" />
            </div>
            <div>
              <label className={LABEL_CLS}>Location</label>
              <input className={INPUT_CLS} value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="City, Country" />
            </div>
            <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-2">
              {isAdmin ? (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form.suggested}
                    onChange={e => setForm(f => ({ ...f, suggested: e.target.checked }))} className="rounded" />
                  Mark as Suggested for the team
                </label>
              ) : <span />}
              <div className="flex gap-2">
                <button type="button" className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm" onClick={() => { setShowManual(false); setForm(EMPTY_FORM); }}>Cancel</button>
                <button type="submit" disabled={saving || !form.company_name.trim()} className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded hover:bg-[#587f2b] disabled:opacity-50 text-sm">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* AI find / paste */}
      <CompetitorFinder isAdmin={isAdmin} existingRows={rows} onAdd={addFromFinder} />

      {/* Main list table */}
      <div className="bg-white rounded shadow border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-900">All Leads ({rows.length}) <span className="text-sm font-normal text-gray-500">· {pendingCount} to review</span></h3>
        </div>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#679632]"></div>
            <p className="mt-3 text-gray-600 text-sm">Loading leads...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-600">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className={TH_CLS}>Company</th>
                  <th className={TH_CLS}>Competitor of</th>
                  <th className={TH_CLS}>Core business</th>
                  <th className={TH_CLS}>Added by</th>
                  <th className={TH_CLS}>Source</th>
                  <th className={TH_CLS}>Status</th>
                  <th className={TH_CLS}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500 text-sm">No leads yet. Add one, find with AI, or paste a list above.</td></tr>
                ) : rows.map(row => {
                  const done = row.status === 'imported' || row.status === 'duplicate';
                  const busy = !!actionLoading[row.id];
                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className={`${TD_CLS} font-medium`}>
                        <div className="flex items-center gap-2">
                          <span>{row.company_name}</span>
                          {row.suggested && <Star size={13} className="fill-amber-400 text-amber-400" title="Suggested" />}
                          {row.company_url && <a href={row.company_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600" title="Website"><ExternalLink size={13} /></a>}
                          {row.linkedin_url && <a href={row.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600" title="LinkedIn">in</a>}
                        </div>
                      </td>
                      <td className={`${TD_CLS} text-gray-600`}>{row.related_to || '-'}</td>
                      <td className={`${TD_CLS} text-gray-600`}>{row.core_business || '-'}</td>
                      <td className={`${TD_CLS} text-gray-600`}>
                        {row.added_by_name || '-'}
                        {row.added_by_role && <span className="text-gray-400"> · {(row.added_by_role || '').toLowerCase() === 'admin' ? 'Admin' : 'BDA'}</span>}
                      </td>
                      <td className={TD_CLS}>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${row.source === 'gemini' ? 'bg-[#e1f0da] text-[#587f2b]' : 'bg-gray-100 text-gray-600'}`}>
                          {row.source === 'gemini' ? 'AI' : 'Manual'}
                        </span>
                      </td>
                      <td className={TD_CLS}>
                        <span className={`px-2 py-1 rounded-full text-xs ${statusPill(row.status)}`}>{statusLabel(row.status)}</span>
                      </td>
                      <td className={TD_CLS}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => importRow(row, myBde?.id ?? null)}
                            disabled={done || busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#679632] text-white rounded hover:bg-[#587f2b] text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                            title={myBde ? `Import to Pre-Sales, assigned to ${myBde.name}` : 'Import to Pre-Sales'}
                          >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                            {row.status === 'imported' ? 'Imported' : row.status === 'duplicate' ? 'Duplicate' : 'Import'}
                          </button>
                          <select
                            defaultValue=""
                            disabled={done || busy}
                            onChange={(e) => importRow(row, e.target.value ? parseInt(e.target.value, 10) : null)}
                            className="text-xs border border-gray-300 rounded px-1 py-1 disabled:bg-gray-50 disabled:text-gray-400"
                            title="Import and assign to a BDA"
                          >
                            <option value="">Assign</option>
                            {bdas.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                          <button onClick={() => remove(row)} disabled={busy} className="text-gray-400 hover:text-red-600 disabled:opacity-40" title="Remove">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Competitor finder: AI web search or paste-a-list, results shown as a table
function CompetitorFinder({ isAdmin, existingRows, onAdd }) {
  const [mode, setMode] = useState('search'); // 'search' | 'paste'
  const [company, setCompany] = useState('');
  const [url, setUrl] = useState('');
  const [location, setLocation] = useState('');
  const [count, setCount] = useState(6);
  const [pasteText, setPasteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [addedKeys, setAddedKeys] = useState(new Set());
  const [addingKey, setAddingKey] = useState(null);
  const [suggestAll, setSuggestAll] = useState(false);

  const existingNames = useMemo(
    () => new Set((existingRows || []).map(r => (r.company_name || '').toLowerCase().trim())),
    [existingRows]
  );

  const runSearch = async () => {
    if (!company.trim()) return;
    setLoading(true); setError(null); setResults(null); setAddedKeys(new Set());
    try {
      const r = await stagingLeadsAPI.findCompetitors({ companyName: company, companyUrl: url, location, count });
      if (!r?.success) throw new Error(r?.error || 'Search failed');
      setResults(r.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const runParse = async () => {
    if (!pasteText.trim()) return;
    setLoading(true); setError(null); setResults(null); setAddedKeys(new Set());
    try {
      const r = await stagingLeadsAPI.parsePaste({ text: pasteText, relatedTo: company });
      if (!r?.success) throw new Error(r?.error || 'Could not read that list');
      setResults(r.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const addOne = async (cand) => {
    const key = (cand.company_name || '').toLowerCase().trim();
    setAddingKey(key);
    try {
      await onAdd(cand, { suggested: isAdmin && suggestAll, relatedTo: company });
      setAddedKeys(prev => new Set(prev).add(key));
    } catch (e) { alert(`Add failed: ${e.response?.data?.error || e.message}`); }
    finally { setAddingKey(null); }
  };

  const addAll = async () => {
    for (const c of results || []) {
      const key = (c.company_name || '').toLowerCase().trim();
      if (addedKeys.has(key) || existingNames.has(key)) continue;
      // eslint-disable-next-line no-await-in-loop
      await addOne(c);
    }
  };

  const switchMode = (m) => { setMode(m); setResults(null); setError(null); };
  const tabCls = (m) => `px-3 py-1.5 text-sm font-medium rounded ${mode === m ? 'bg-[#679632] text-white' : 'text-gray-600 hover:bg-gray-100'}`;

  return (
    <div className="bg-white rounded shadow border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Add competitors in bulk</h3>
          <p className="text-xs text-gray-500 mt-0.5">Have the app find competitors, or paste a list you already have.</p>
        </div>
        <div className="inline-flex items-center gap-1 bg-gray-100 rounded p-1">
          <button className={tabCls('search')} onClick={() => switchMode('search')}>Find with AI</button>
          <button className={tabCls('paste')} onClick={() => switchMode('paste')}>Paste a list</button>
        </div>
      </div>

      <div className="p-4">
        {mode === 'search' ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className={LABEL_CLS}>Company to find competitors of</label>
              <input className={INPUT_CLS} value={company} onChange={e => setCompany(e.target.value)}
                placeholder="e.g. H2air" onKeyDown={e => { if (e.key === 'Enter') runSearch(); }} />
            </div>
            <div>
              <label className={LABEL_CLS}>Website (optional)</label>
              <input className={INPUT_CLS} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://" />
            </div>
            <div>
              <label className={LABEL_CLS}>Region (optional)</label>
              <input className={INPUT_CLS} value={location} onChange={e => setLocation(e.target.value)} placeholder="US, France, India" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            <div className="md:w-1/3">
              <label className={LABEL_CLS}>These are competitors of (optional)</label>
              <input className={INPUT_CLS} value={company} onChange={e => setCompany(e.target.value)} placeholder="e.g. H2air" />
            </div>
            <div>
              <label className={LABEL_CLS}>Paste your list</label>
              <textarea
                className={`${INPUT_CLS} h-36 resize-y font-mono text-xs`}
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                placeholder={"Paste a table, bullet list, or a whole competitive landscape here.\n\nSpirit Energy   UK commercial solar EPC   High\nCustom Solar    Commercial solar EPC      High"}
              />
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {mode === 'search' && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">How many</label>
              <select value={count} onChange={e => setCount(parseInt(e.target.value, 10))} className="text-sm border border-gray-300 rounded px-2 py-1.5">
                {[3, 6, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          )}
          <button
            onClick={mode === 'search' ? runSearch : runParse}
            disabled={loading || (mode === 'search' ? !company.trim() : !pasteText.trim())}
            className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded hover:bg-[#587f2b] disabled:opacity-50 text-sm"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : mode === 'search' ? <Search size={16} /> : <ClipboardList size={16} />}
            {loading ? (mode === 'search' ? 'Looking...' : 'Reading...') : (mode === 'search' ? 'Find competitors' : 'Pull out the companies')}
          </button>
          {isAdmin && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={suggestAll} onChange={e => setSuggestAll(e.target.checked)} className="rounded" />
              Add as Suggested
            </label>
          )}
          {loading && <span className="text-xs text-gray-500">{mode === 'search' ? 'Can take up to a minute or two.' : 'Just a few seconds.'}</span>}
        </div>

        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
      </div>

      {results && (
        <div className="border-t border-gray-200">
          <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-600">
              {results.length} found{company.trim() ? ` for ${company}` : ''}
            </span>
            {results.length > 0 && (
              <button onClick={addAll} className="text-xs text-[#679632] hover:underline font-medium">Add all new</button>
            )}
          </div>
          {results.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500">
              {mode === 'search' ? 'Nothing came back. Try a different name, or add a website and region.' : 'No company names found in that text. Try pasting a bit more.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={TH_CLS}>Company</th>
                    <th className={TH_CLS}>Location</th>
                    <th className={TH_CLS}>Notes</th>
                    <th className={TH_CLS}>Status</th>
                    <th className={TH_CLS}>Add</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {results.map((c, i) => {
                    const key = (c.company_name || '').toLowerCase().trim();
                    const added = addedKeys.has(key);
                    const already = existingNames.has(key);
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className={`${TD_CLS} font-medium`}>
                          <div className="flex items-center gap-2">
                            <span>{c.company_name}</span>
                            {c.company_url && <a href={c.company_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600" title="Website"><ExternalLink size={13} /></a>}
                            {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600" title="LinkedIn">in</a>}
                          </div>
                        </td>
                        <td className={`${TD_CLS} text-gray-600`}>{c.location || '-'}</td>
                        <td className={`${TD_CLS} text-gray-600`}>{c.why || c.core_business || '-'}</td>
                        <td className={TD_CLS}>
                          {c.already_in_pipeline
                            ? <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700" title="Already in the pipeline — will be skipped on import">In {String(c.already_in_pipeline).replace('_', ' ')}</span>
                            : <span className="px-2 py-0.5 rounded-full text-xs bg-[#e1f0da] text-[#587f2b]">New</span>}
                        </td>
                        <td className={TD_CLS}>
                          <button
                            onClick={() => addOne(c)}
                            disabled={added || already || addingKey === key}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs ${(added || already) ? 'bg-gray-100 text-gray-500 cursor-default' : 'bg-[#679632] text-white hover:bg-[#587f2b]'} disabled:opacity-60`}
                          >
                            {addingKey === key ? <Loader2 size={12} className="animate-spin" /> : null}
                            {added ? 'Added' : already ? 'In list' : 'Add'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
